import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Ban, 
  CheckCircle,
  Mail,
  Phone,
  Building,
  Calendar,
  Key,
  Bell,
  BellOff
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';

const AdminUsers = () => {
  const { adminToken } = useAdminAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filters, setFilters] = useState({
    userType: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [userForm, setUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    userType: 'private',
    status: 'active',
    emailVerified: false,
    marketingConsent: false,
    // Business fields
    companyName: '',
    vatNumber: '',
    fiscalCode: '',
    pec: '',
    businessType: '',
    codiceUnivoco: '',
    website: '',
    description: '',
    // Address fields (billing/main address)
    address: '',
    city: '',
    province: '',
    zipCode: '',
    country: 'Italy',
    // Shipping address flag and fields
    hasSeparateShippingAddress: false,
    shippingAddress: '',
    shippingCity: '',
    shippingProvince: '',
    shippingZipCode: '',
    shippingCountry: 'Italy',
    shippingFirstName: '',
    shippingLastName: '',
    shippingPhone: '',
    shippingNotes: ''
  });

  useEffect(() => {
    loadUsers();
  }, [filters, pagination.page]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters.search, filters.userType, filters.status]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // Call real admin API for users with filters
      const { adminAPI } = await import('../../services/api');
      const result = await adminAPI.getUsers(pagination.page, pagination.limit, filters);
      
      if (result.success) {
        setUsers(result.data || []);
        setPagination(prev => ({ 
          ...prev, 
          total: result.pagination?.total || 0 
        }));
      } else {
        throw new Error(result.message || 'Errore nel caricamento utenti');
      }
      
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: "Errore nel caricamento",
        description: error.response?.data?.detail || "Impossibile caricare gli utenti",
        variant: "destructive"
      });
      
      // Fallback to empty state if API fails
      setUsers([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updateData) => {
    try {
      setUpdating(userId);
      
      // Call real API to update user
      const { adminAPI } = await import('../../services/api');
      const result = await adminAPI.updateUser(userId, updateData);
      
      if (result.success) {
        // Update user in local state
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, ...result.data, updatedAt: new Date().toISOString() }
            : user
        ));
        
        toast({
          title: "Utente aggiornato",
          description: "Le modifiche sono state salvate con successo",
        });
        
        setShowEditModal(false);
        setSelectedUser(null);
      } else {
        throw new Error(result.message || 'Errore nell\'aggiornamento');
      }
      
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: "Errore nell'aggiornamento",
        description: error.response?.data?.detail || error.message || "Impossibile aggiornare l'utente",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setUpdating('creating');
      
      // Call real API to create user
      const { adminAPI } = await import('../../services/api');
      const result = await adminAPI.createUser(userData);
      
      if (result.success) {
        const newUser = {
          ...result.data,
          ordersCount: 0,
          totalSpent: 0
        };
        
        setUsers(prev => [newUser, ...prev]);
        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
        
        // Show temporary password to admin
        if (result.temporaryPassword) {
          toast({
            title: "Utente creato con successo",
            description: `Password temporanea: ${result.temporaryPassword}. Comunicala all'utente in modo sicuro.`,
            duration: 10000  // Show for 10 seconds
          });
        } else {
          toast({
            title: "Utente creato",
            description: `Utente ${userData.firstName} ${userData.lastName} creato con successo`,
          });
        }
        
        setShowCreateModal(false);
        resetForm();
      } else {
        throw new Error(result.message || 'Errore nella creazione');
      }
      
    } catch (error) {
      console.error('Error creating user:', error);
      toast({
        title: "Errore nella creazione",
        description: error.response?.data?.detail || error.message || "Impossibile creare l'utente",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      setUpdating(userId);
      
      // Mock API call - in production would call DELETE /api/auth/admin/users/{userId}
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUsers(prev => prev.filter(user => user.id !== userId));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      
      toast({
        title: "Utente eliminato",
        description: "L'utente è stato eliminato dal sistema",
      });
      
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Errore nell'eliminazione",
        description: "Impossibile eliminare l'utente",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleCRM = async (userId, currentStatus) => {
    const action = currentStatus ? 'disattivare' : 'attivare';
    const endpoint = currentStatus ? 'deactivate-crm' : 'activate-crm';
    
    if (!window.confirm(`Sei sicuro di voler ${action} questo utente nel CRM?`)) {
      return;
    }

    try {
      setUpdating(userId);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${userId}/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Errore nella richiesta');
      }

      const data = await response.json();
      
      // Aggiorna lo stato locale
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, isCrmActive: !currentStatus }
          : user
      ));
      
      toast({
        title: currentStatus ? "Utente rimosso dal CRM" : "Utente aggiunto al CRM",
        description: data.message || "Operazione completata con successo",
      });
      
    } catch (error) {
      console.error('Error toggling CRM:', error);
      toast({
        title: "Errore nell'operazione",
        description: "Impossibile modificare lo stato CRM",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleOrderNotifications = async (userId, currentStatus) => {
    const action = currentStatus ? 'disattivare' : 'attivare';
    
    if (!window.confirm(`Sei sicuro di voler ${action} le notifiche ordini per questo admin?`)) {
      return;
    }

    try {
      setUpdating(userId);
      
      // Use the generic update endpoint
      const { adminAPI } = await import('../../services/api');
      const result = await adminAPI.updateUser(userId, {
        receiveOrderNotifications: !currentStatus
      });
      
      if (result.success) {
        // Update local state
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, receiveOrderNotifications: !currentStatus }
            : user
        ));
        
        toast({
          title: currentStatus ? "Notifiche ordini disattivate" : "Notifiche ordini attivate",
          description: "Le preferenze di notifica sono state aggiornate con successo",
        });
      } else {
        throw new Error(result.message || 'Errore nell\'aggiornamento');
      }
      
    } catch (error) {
      console.error('Error toggling order notifications:', error);
      toast({
        title: "Errore nell'operazione",
        description: error.response?.data?.detail || error.message || "Impossibile modificare le notifiche",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const openResetPasswordModal = (user) => {
    setResetPasswordUser(user);
    setNewPassword(generateRandomPassword());
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) {
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: "Password troppo corta",
        description: "La password deve essere almeno 8 caratteri",
        variant: "destructive"
      });
      return;
    }

    try {
      setUpdating(resetPasswordUser.id);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${resetPasswordUser.id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify({ newPassword })
        }
      );

      if (response.ok) {
        const result = await response.json();
        
        toast({
          title: "Password reimpostata",
          description: `Password per ${resetPasswordUser.email} reimpostata con successo`,
        });

        // Show the new password to admin
        alert(`Password reimpostata per ${resetPasswordUser.email}:\n\n${newPassword}\n\nComunica questa password all'utente in modo sicuro.`);
        
        setShowResetPasswordModal(false);
        setResetPasswordUser(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Errore reset password');
      }
      
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Errore reset password",
        description: error.message || "Impossibile reimpostare la password",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const getUserTypeIcon = (userType) => {
    switch (userType) {
      case 'admin':
        return <CheckCircle className="w-4 h-4 text-red-600" />;
      case 'business':
        return <Building className="w-4 h-4 text-blue-600" />;
      case 'private':
        return <Users className="w-4 h-4 text-green-600" />;
      default:
        return <Users className="w-4 h-4 text-gray-600" />;
    }
  };

  const getUserTypeText = (userType) => {
    switch (userType) {
      case 'admin': return 'Admin';
      case 'business': return 'Business';
      case 'private': return 'Privato';
      default: return userType;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    // Extract business info from nested businessInfo object
    const businessInfo = user.businessInfo || {};
    
    // Format dateOfBirth if exists
    let dateOfBirthFormatted = '';
    if (user.dateOfBirth) {
      try {
        const date = new Date(user.dateOfBirth);
        dateOfBirthFormatted = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      } catch (e) {
        console.error('Error formatting date:', e);
      }
    }
    
    // Extract shipping address if exists
    const shippingAddr = user.customerAddresses?.shippingAddress || {};
    const hasSeparateShipping = !!(shippingAddr.address && shippingAddr.city);
    
    setUserForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      dateOfBirth: dateOfBirthFormatted,
      userType: user.userType || 'private',
      status: user.status || 'active',
      emailVerified: user.emailVerified || false,
      marketingConsent: user.marketingConsent || false,
      companyName: businessInfo.companyName || '',
      vatNumber: businessInfo.vatNumber || '',
      fiscalCode: businessInfo.fiscalCode || '',
      pec: businessInfo.pec || '',
      businessType: businessInfo.businessType || '',
      codiceUnivoco: businessInfo.codiceUnivoco || '',
      website: businessInfo.website || '',
      description: businessInfo.description || '',
      address: (typeof user.address === 'object' && user.address ? user.address.street : user.address) || businessInfo.address || '',
      city: (typeof user.address === 'object' && user.address ? user.address.city : user.city) || businessInfo.city || '',
      province: (typeof user.address === 'object' && user.address ? user.address.province : user.province) || businessInfo.province || '',
      zipCode: (typeof user.address === 'object' && user.address ? user.address.zipcode : (user.zipCode || user.zipcode)) || businessInfo.zipCode || '',
      country: (typeof user.address === 'object' && user.address ? user.address.country : user.country) || businessInfo.country || 'Italy',
      // Shipping address
      hasSeparateShippingAddress: hasSeparateShipping,
      shippingAddress: shippingAddr.address || '',
      shippingCity: shippingAddr.city || '',
      shippingProvince: shippingAddr.province || '',
      shippingZipCode: shippingAddr.postalCode || '',
      shippingCountry: shippingAddr.country || 'Italy',
      shippingFirstName: shippingAddr.firstName || user.firstName || '',
      shippingLastName: shippingAddr.lastName || user.lastName || '',
      shippingPhone: shippingAddr.phone || user.phone || '',
      shippingNotes: shippingAddr.notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setUserForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      userType: 'private',
      status: 'active',
      emailVerified: false,
      marketingConsent: false,
      companyName: '',
      vatNumber: '',
      fiscalCode: '',
      pec: '',
      businessType: '',
      codiceUnivoco: '',
      website: '',
      description: '',
      address: '',
      city: '',
      province: '',
      zipCode: '',
      country: 'Italy',
      hasSeparateShippingAddress: false,
      shippingAddress: '',
      shippingCity: '',
      shippingProvince: '',
      shippingZipCode: '',
      shippingCountry: 'Italy',
      shippingFirstName: '',
      shippingLastName: '',
      shippingPhone: '',
      shippingNotes: ''
    });
  };

  // No need for client-side filtering anymore, backend handles it
  const filteredUsers = users;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Utenti</h1>
            <p className="mt-2 text-gray-600">Gestisci tutti gli utenti registrati nel sistema</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Utente
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cerca</label>
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Nome, email, azienda..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Utente</label>
              <select
                value={filters.userType}
                onChange={(e) => setFilters(prev => ({ ...prev, userType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tutti i tipi</option>
                <option value="private">Privato</option>
                <option value="business">Business</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stato</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tutti gli stati</option>
                <option value="active">Attivo</option>
                <option value="inactive">Inattivo</option>
                <option value="suspended">Sospeso</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ userType: '', status: '', search: '' })}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Pulisci Filtri
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <LoadingSpinner message="Caricamento utenti..." />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stato
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CRM
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notifiche
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ordini
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ultimo Accesso
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {user.firstName[0]}{user.lastName[0]}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <button
                                onClick={() => navigate(`/admin/users/${user.id}`)}
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                              >
                                {user.firstName} {user.lastName}
                              </button>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Mail className="w-3 h-3 mr-1" />
                                {user.email}
                              </div>
                              {user.phone && (
                                <div className="text-sm text-gray-500 flex items-center">
                                  <Phone className="w-3 h-3 mr-1" />
                                  {user.phone}
                                </div>
                              )}
                              {user.companyName && (
                                <div className="text-sm text-gray-500 flex items-center">
                                  <Building className="w-3 h-3 mr-1" />
                                  {user.companyName}
                                </div>
                              )}
                              {(() => {
                                // L'indirizzo può essere una stringa (formato legacy) o un oggetto
                                // {street, city, province, zipcode, country} (agenti signup pubblico).
                                const addr = user.address;
                                const isAddrObject = addr && typeof addr === 'object';
                                const address = isAddrObject ? addr.street : (addr || user.businessInfo?.address);
                                const city = (isAddrObject ? addr.city : user.city) || user.businessInfo?.city;
                                const zipCode = (isAddrObject ? addr.zipcode : (user.zipCode || user.zipcode)) || user.businessInfo?.zipCode;
                                const province = (isAddrObject ? addr.province : user.province) || user.businessInfo?.province;

                                if (!address && !city && !zipCode) return null;
                                
                                return (
                                  <div className="text-sm text-gray-500 flex items-start mt-1">
                                    <svg className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span className="flex-1">
                                      {address}
                                      {(city || zipCode) && (
                                        <span>, {zipCode} {city} {province && `(${province})`}</span>
                                      )}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getUserTypeIcon(user.userType)}
                            <span className="ml-2 text-sm text-gray-900">
                              {getUserTypeText(user.userType)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status)}`}>
                            {user.status}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {user.emailVerified ? '✅ Email verificata' : '❌ Email non verificata'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.isCrmActive ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              ✓ Attivo
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              Non attivo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.userType === 'admin' ? (
                            user.receiveOrderNotifications ? (
                              <div className="flex items-center">
                                <Bell className="w-4 h-4 text-green-600 mr-1" />
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  ✓ Attive
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center">
                                <BellOff className="w-4 h-4 text-gray-400 mr-1" />
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                  Disattivate
                                </span>
                              </div>
                            )
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{user.ordersCount} ordini</div>
                          <div className="text-xs text-gray-500">€{user.totalSpent.toFixed(2)} totale</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.lastLogin ? (
                            <>
                              <div>{new Date(user.lastLogin).toLocaleDateString('it-IT')}</div>
                              <div className="text-xs">{new Date(user.lastLogin).toLocaleTimeString('it-IT')}</div>
                            </>
                          ) : (
                            <span className="text-gray-400">Mai</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {user.userType !== 'admin' && (
                              <button
                                onClick={() => handleToggleCRM(user.id, user.isCrmActive)}
                                disabled={updating === user.id}
                                className={`${
                                  user.isCrmActive 
                                    ? 'text-orange-600 hover:text-orange-900' 
                                    : 'text-green-600 hover:text-green-900'
                                } p-1 rounded`}
                                title={user.isCrmActive ? "Rimuovi dal CRM" : "Aggiungi al CRM"}
                              >
                                {user.isCrmActive ? (
                                  <Ban className="w-4 h-4" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            {user.userType === 'admin' && (
                              <button
                                onClick={() => handleToggleOrderNotifications(user.id, user.receiveOrderNotifications)}
                                disabled={updating === user.id}
                                className={`${
                                  user.receiveOrderNotifications 
                                    ? 'text-orange-600 hover:text-orange-900' 
                                    : 'text-green-600 hover:text-green-900'
                                } p-1 rounded`}
                                title={user.receiveOrderNotifications ? "Disattiva notifiche ordini" : "Attiva notifiche ordini"}
                              >
                                {user.receiveOrderNotifications ? (
                                  <BellOff className="w-4 h-4" />
                                ) : (
                                  <Bell className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => openEditModal(user)}
                              disabled={updating === user.id}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="Modifica utente"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {user.userType !== 'admin' && (
                              <button
                                onClick={() => openResetPasswordModal(user)}
                                disabled={updating === user.id}
                                className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                                title="Reimposta password"
                              >
                                <Key className="w-4 h-4" />
                              </button>
                            )}
                            {user.userType !== 'admin' && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={updating === user.id}
                                className="text-red-600 hover:text-red-900 p-1 rounded"
                                title="Elimina utente"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun utente trovato</h3>
                  <p className="mt-1 text-sm text-gray-500">Prova a modificare i filtri di ricerca</p>
                </div>
              )}

              {/* Pagination Controls */}
              {pagination.total > pagination.limit && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Precedente
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Successiva
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> a{' '}
                        <span className="font-medium">
                          {Math.min(pagination.page * pagination.limit, pagination.total)}
                        </span>{' '}
                        di <span className="font-medium">{pagination.total}</span> risultati
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                          disabled={pagination.page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Precedente</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: Math.ceil(pagination.total / pagination.limit) }, (_, i) => i + 1)
                          .filter(pageNum => {
                            const totalPages = Math.ceil(pagination.total / pagination.limit);
                            if (totalPages <= 7) return true;
                            if (pageNum === 1 || pageNum === totalPages) return true;
                            if (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1) return true;
                            return false;
                          })
                          .map((pageNum, index, array) => {
                            const showEllipsis = index > 0 && pageNum - array[index - 1] > 1;
                            return (
                              <React.Fragment key={pageNum}>
                                {showEllipsis && (
                                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    ...
                                  </span>
                                )}
                                <button
                                  onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    pagination.page === pageNum
                                      ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              </React.Fragment>
                            );
                          })}
                        
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, page: Math.min(Math.ceil(pagination.total / pagination.limit), prev.page + 1) }))}
                          disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Successiva</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Edit/Create Modal */}
        {(showEditModal || showCreateModal) && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {showCreateModal ? 'Crea Nuovo Utente' : `Modifica Utente: ${selectedUser?.firstName} ${selectedUser?.lastName}`}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {/* Basic Info */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input
                      type="text"
                      value={userForm.firstName}
                      onChange={(e) => setUserForm(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                    <input
                      type="text"
                      value={userForm.lastName}
                      onChange={(e) => setUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                    <input
                      type="tel"
                      value={userForm.phone}
                      onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                    <input
                      type="date"
                      value={userForm.dateOfBirth}
                      onChange={(e) => setUserForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Utente</label>
                    <select
                      value={userForm.userType}
                      onChange={(e) => setUserForm(prev => ({ ...prev, userType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="private">Privato</option>
                      <option value="business">Business</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
                    <select
                      value={userForm.status}
                      onChange={(e) => setUserForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="active">Attivo</option>
                      <option value="inactive">Inattivo</option>
                      <option value="suspended">Sospeso</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userForm.emailVerified}
                          onChange={(e) => setUserForm(prev => ({ ...prev, emailVerified: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Email verificata</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userForm.marketingConsent}
                          onChange={(e) => setUserForm(prev => ({ ...prev, marketingConsent: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Consenso marketing</span>
                      </label>
                    </div>
                  </div>

                  {/* Address Fields - For both Private and Business users */}
                  {(userForm.userType === 'private' || userForm.userType === 'business') && (
                    <>
                      <div className="md:col-span-2">
                        <h4 className="text-md font-semibold text-gray-900 mb-3 mt-4 border-t pt-4">
                          📍 Indirizzo {userForm.userType === 'business' ? 'Sede Legale' : 'Residenza'}
                        </h4>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo (Via e Numero Civico)</label>
                        <input
                          type="text"
                          value={userForm.address || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, address: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Via Roma, 123"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                        <input
                          type="text"
                          value={userForm.city || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Milano"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                        <input
                          type="text"
                          value={userForm.zipCode || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, zipCode: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="20100"
                          maxLength="5"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                        <input
                          type="text"
                          value={userForm.province || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, province: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="MI"
                          maxLength="2"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Paese</label>
                        <input
                          type="text"
                          value={userForm.country || 'Italy'}
                          onChange={(e) => setUserForm(prev => ({ ...prev, country: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Shipping Address Toggle */}
                      <div className="md:col-span-2 mt-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userForm.hasSeparateShippingAddress}
                            onChange={(e) => setUserForm(prev => ({ 
                              ...prev, 
                              hasSeparateShippingAddress: e.target.checked,
                              // Pre-fill with main address data if enabling
                              shippingFirstName: e.target.checked && !prev.shippingFirstName ? prev.firstName : prev.shippingFirstName,
                              shippingLastName: e.target.checked && !prev.shippingLastName ? prev.lastName : prev.shippingLastName,
                              shippingPhone: e.target.checked && !prev.shippingPhone ? prev.phone : prev.shippingPhone
                            }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            📦 Indirizzo di spedizione diverso
                          </span>
                        </label>
                      </div>

                      {/* Separate Shipping Address Fields */}
                      {userForm.hasSeparateShippingAddress && (
                        <>
                          <div className="md:col-span-2">
                            <h5 className="text-sm font-semibold text-gray-800 mb-2 mt-2">📦 Dati Spedizione</h5>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome (Destinatario)</label>
                            <input
                              type="text"
                              value={userForm.shippingFirstName || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingFirstName: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cognome (Destinatario)</label>
                            <input
                              type="text"
                              value={userForm.shippingLastName || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingLastName: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo Spedizione</label>
                            <input
                              type="text"
                              value={userForm.shippingAddress || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingAddress: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Via della Spedizione, 456"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                            <input
                              type="text"
                              value={userForm.shippingCity || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingCity: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                            <input
                              type="text"
                              value={userForm.shippingZipCode || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingZipCode: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              maxLength="5"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                            <input
                              type="text"
                              value={userForm.shippingProvince || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingProvince: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              maxLength="2"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Paese</label>
                            <input
                              type="text"
                              value={userForm.shippingCountry || 'Italy'}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingCountry: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Telefono Spedizione</label>
                            <input
                              type="tel"
                              value={userForm.shippingPhone || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingPhone: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Note Spedizione</label>
                            <textarea
                              value={userForm.shippingNotes || ''}
                              onChange={(e) => setUserForm(prev => ({ ...prev, shippingNotes: e.target.value }))}
                              rows="2"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Es: Consegnare al portiere, campanello 3° piano..."
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Business Fields */}
                  {userForm.userType === 'business' && (
                    <>
                      <div className="md:col-span-2">
                        <h4 className="text-md font-semibold text-gray-900 mb-3 mt-4 border-t pt-4">💼 Informazioni Azienda</h4>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome Azienda</label>
                        <input
                          type="text"
                          value={userForm.companyName}
                          onChange={(e) => setUserForm(prev => ({ ...prev, companyName: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
                        <input
                          type="text"
                          value={userForm.vatNumber}
                          onChange={(e) => setUserForm(prev => ({ ...prev, vatNumber: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                        <input
                          type="text"
                          value={userForm.fiscalCode || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, fiscalCode: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PEC</label>
                        <input
                          type="email"
                          value={userForm.pec || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, pec: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Azienda</label>
                        <select
                          value={userForm.businessType || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, businessType: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Seleziona tipo</option>
                          <option value="srl">S.r.l.</option>
                          <option value="spa">S.p.A.</option>
                          <option value="snc">S.n.c.</option>
                          <option value="sas">S.a.s.</option>
                          <option value="ditta_individuale">Ditta Individuale</option>
                          <option value="cooperativa">Cooperativa</option>
                          <option value="altro">Altro</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Codice Univoco (SDI)</label>
                        <input
                          type="text"
                          value={userForm.codiceUnivoco || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, codiceUnivoco: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Codice destinatario SDI"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sito Web</label>
                        <input
                          type="url"
                          value={userForm.website || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, website: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://www.esempio.it"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione Azienda</label>
                        <textarea
                          value={userForm.description || ''}
                          onChange={(e) => setUserForm(prev => ({ ...prev, description: e.target.value }))}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Breve descrizione dell'attività..."
                        />
                      </div>
                    </>
                  )}

                  {/* Customer Addresses Section - Only for editing existing users */}
                  {showEditModal && selectedUser && (
                    <div className="md:col-span-2 mt-6 border-t pt-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">Indirizzi Cliente</h4>
                      
                      {/* Shipping Address */}
                      {selectedUser.customerAddresses?.shippingAddress ? (
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <h5 className="font-medium text-gray-700 mb-2">📦 Indirizzo di Spedizione</h5>
                          <div className="text-sm text-gray-600">
                            <p>{selectedUser.customerAddresses.shippingAddress.firstName} {selectedUser.customerAddresses.shippingAddress.lastName}</p>
                            {selectedUser.customerAddresses.shippingAddress.company && (
                              <p className="font-medium">{selectedUser.customerAddresses.shippingAddress.company}</p>
                            )}
                            <p>{selectedUser.customerAddresses.shippingAddress.address}</p>
                            <p>{selectedUser.customerAddresses.shippingAddress.city}, {selectedUser.customerAddresses.shippingAddress.postalCode} ({selectedUser.customerAddresses.shippingAddress.province})</p>
                            <p>{selectedUser.customerAddresses.shippingAddress.country}</p>
                            {selectedUser.customerAddresses.shippingAddress.phone && (
                              <p>Tel: {selectedUser.customerAddresses.shippingAddress.phone}</p>
                            )}
                            {selectedUser.customerAddresses.shippingAddress.notes && (
                              <p className="text-xs italic mt-1">Note: {selectedUser.customerAddresses.shippingAddress.notes}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                          <p className="text-sm text-yellow-700">📦 Nessun indirizzo di spedizione salvato</p>
                        </div>
                      )}

                      {/* Billing Address */}
                      {selectedUser.customerAddresses?.billingAddress && !selectedUser.customerAddresses?.sameAsShipping ? (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-2">🧾 Indirizzo di Fatturazione</h5>
                          <div className="text-sm text-gray-600">
                            <p>{selectedUser.customerAddresses.billingAddress.firstName} {selectedUser.customerAddresses.billingAddress.lastName}</p>
                            {selectedUser.customerAddresses.billingAddress.company && (
                              <p className="font-medium">{selectedUser.customerAddresses.billingAddress.company}</p>
                            )}
                            <p>{selectedUser.customerAddresses.billingAddress.address}</p>
                            <p>{selectedUser.customerAddresses.billingAddress.city}, {selectedUser.customerAddresses.billingAddress.postalCode} ({selectedUser.customerAddresses.billingAddress.province})</p>
                            <p>{selectedUser.customerAddresses.billingAddress.country}</p>
                            {selectedUser.customerAddresses.billingAddress.phone && (
                              <p>Tel: {selectedUser.customerAddresses.billingAddress.phone}</p>
                            )}
                            {selectedUser.customerAddresses.billingAddress.notes && (
                              <p className="text-xs italic mt-1">Note: {selectedUser.customerAddresses.billingAddress.notes}</p>
                            )}
                          </div>
                        </div>
                      ) : selectedUser.customerAddresses?.sameAsShipping ? (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-blue-700">🧾 Indirizzo di fatturazione uguale a quello di spedizione</p>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <p className="text-sm text-yellow-700">🧾 Nessun indirizzo di fatturazione salvato</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setShowCreateModal(false);
                      setSelectedUser(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={() => {
                      if (showCreateModal) {
                        handleCreateUser(userForm);
                      } else {
                        handleUpdateUser(selectedUser.id, userForm);
                      }
                    }}
                    disabled={updating}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updating ? 'Salvando...' : showCreateModal ? 'Crea Utente' : 'Salva Modifiche'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetPasswordModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  🔑 Reimposta Password
                </h3>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Utente: <strong>{resetPasswordUser?.firstName} {resetPasswordUser?.lastName}</strong>
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Email: <strong>{resetPasswordUser?.email}</strong>
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nuova Password
                  </label>
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Inserisci nuova password"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimo 8 caratteri. Una password casuale è stata generata automaticamente.
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Importante:</strong> Comunica questa password all&apos;utente in modo sicuro. 
                    L&apos;utente dovrà cambiarla al primo accesso.
                  </p>
                </div>

                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setResetPasswordUser(null);
                      setNewPassword('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={updating || !newPassword || newPassword.length < 8}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 disabled:opacity-50"
                  >
                    {updating ? 'Reimpostando...' : 'Reimposta Password'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;